/**
 * Tablero Principal Público (Dashboard Main).
 * Presenta estadísticas globales, mapas interactivos de laboratorios, monitoreo de riesgos
 * y noticias relevantes sobre geohelmintiasis.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Apiurl } from "../../shared/services/apirest";

// Estilos y Activos (Assets)
import "../../assets/css/Dashboardadmain.css";
import LogoGeo from "../../assets/img/LogoGeo.png";
import CustomSlider from "../../shared/components/CustomSlider";
import SliderMonitoring from "../../assets/img/slider_monitoring.png";
import LogosFooter from "../../shared/components/LogosFooter";
import TestResultIcon from "../../assets/img/test-result.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


// Datos de noticias para el carrusel informativo
const newsSliderData = [
  { title: "Geohelmintiasis transmitidas por el suelo", desc: "Información detallada de la OMS sobre las enfermedades.", link: "https://www.who.int/health-topics/soil-transmitted-helminthiases#tab=tab_1" },
  { title: "Datos de ETD - OMS", desc: "Base de datos y estadísticas globales sobre enfermedades tropicales desatendidas.", link: "https://apps.who.int/neglected_diseases/ntddata/sth/sth.html" },
  { title: "Registro Epidemiológico Semanal", desc: "Publicaciones e informes recientes de la OMS.", link: "https://www.who.int/publications/i/item/who-wer9748-621-632" },
  { title: "Fundación Mundo Sano", desc: "Iniciativas para cambiar la realidad de las enfermedades desatendidas.", link: "https://mundosano.org/es/" },
  { title: "Evidence Action", desc: "Oportunidades y acciones basadas en evidencia para la salud global.", link: "https://www.evidenceaction.org/about/careers" },
  { title: "ISGlobal - Quiénes Somos", desc: "Instituto de Salud Global de Barcelona y su misión.", link: "https://www.isglobal.org/quienes-somos" },
  { title: "Máster en Enfermedades Parasitarias", desc: "Investigación y control de helmintiasis transmitidas por el suelo.", link: "https://www.uv.es/uvweb/master-enfermedades-parasitarias-tropicales/es/blog/-son-helmintiasis-transmitidas-suelo-1285952142283/GasetaRecerca.html?id=1285959449322" },
  { title: "Geohelmintiasis - OPS", desc: "Información de la Organización Panamericana de la Salud.", link: "https://www.paho.org/es/temas/geohelmintiasis" }
];

const municipiosCordoba = [
  "Ayapel", "Buenavista", "Canalete", "Cereté", "Chimá", "Chinú", "Ciénaga de Oro", "Cotorra",
  "La Apartada", "Lorica", "Los Córdobas", "Momil", "Montelíbano", "Montería", "Moñitos",
  "Planeta Rica", "Pueblo Nuevo", "Puerto Escondido", "Puerto Libertador", "Purísima",
  "Sahagún", "San Andrés de Sotavento", "San Antero", "San Bernardo del Viento",
  "San Carlos", "San José de Uré", "San Pelayo", "Tierralta", "Tuchín", "Valencia"
];

function Dashboardmain() {
  const [casos, setCasos] = useState([]);
  const [laboratorios, setLaboratorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);

  const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);


  const [filters, setFilters] = useState({
    fecha: "último mes",
    municipio: "Todos",
    riesgo: "Todos"
  });

  const [currentNews, setCurrentNews] = useState(0);

  // Intervalo para el cambio automático de noticias
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentNews((prev) => (prev + 1) % newsSliderData.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Carga inicial de datos desde la API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Carga de Casos (Acceso público)
        const res = await axios.get(`${Apiurl}casos/`);
        setCasos(Array.isArray(res.data) ? res.data : []);

        // Carga de Laboratorios Registrados
        try {
          const resLabs = await axios.get(`${Apiurl}users/List_Laboratories_Public/`);
          setLaboratorios(Array.isArray(resLabs.data) ? resLabs.data : []);
        } catch (labErr) {
          console.error("Error al cargar laboratorios:", labErr);
        }

      } catch (err) {
        console.error("Error en la carga de datos:", err);
        setError(`No se pudieron cargar los datos del sistema. Por favor intente más tarde.`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter Logic
  const filteredCasos = useMemo(() => {
    let temp = casos;
    if (filters.municipio !== "Todos") {
      temp = temp.filter(c => c.paciente_municipio === filters.municipio);
    }
    if (filters.riesgo !== "Todos") {
      temp = temp.filter(c => c.nivel_peligrosidad?.toUpperCase() === filters.riesgo.toUpperCase());
    }
    // Date filtering logic (simplified for demo)
    return temp;
  }, [casos, filters]);

  // Derived Stats
  const stats = useMemo(() => {
    const total = filteredCasos.length;
    // Count unique municipalities including null as one category "S/M"
    const muniSet = new Set(filteredCasos.map(c => c.paciente_municipio || "S/M"));
    const uniqueMun = muniSet.size;

    const highRisk = filteredCasos.filter(c => c.nivel_peligrosidad?.toUpperCase() === "ALTO").length;
    const mediumRisk = filteredCasos.filter(c => c.nivel_peligrosidad?.toUpperCase() === "MEDIO").length;
    const lowRisk = filteredCasos.filter(c => c.nivel_peligrosidad?.toUpperCase() === "BAJO").length;

    // Alertas Activas should count both Alto and Medio risk
    const activeAlerts = highRisk + mediumRisk;

    return {
      casos: total.toLocaleString(),
      municipios: uniqueMun || 0,
      alertas: activeAlerts,
      zonasRiesgo: highRisk,
      mediumRisk,
      lowRisk
    };
  }, [filteredCasos]);

  // Chart Data Preparation
  const chartData = useMemo(() => {
    const counts = {};
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    // Count real cases by month
    filteredCasos.forEach(c => {
      if (c.fecha_registro) {
        const date = new Date(c.fecha_registro);
        const month = months[date.getMonth()];
        counts[month] = (counts[month] || 0) + 1;
      }
    });
    // Return only months with actual data, or 0 if no data
    return months.map(m => ({ name: m, casos: counts[m] || 0 }));
  }, [filteredCasos]);

  // Municipality Progress Data
  const muniStats = useMemo(() => {
    const mCounts = {};
    filteredCasos.forEach(c => {
      const muni = c.paciente_municipio || "Sin Especificar";
      mCounts[muni] = (mCounts[muni] || 0) + 1;
    });
    return Object.entries(mCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count, percentage: (count / filteredCasos.length) * 100 }));
  }, [filteredCasos]);

  // Transformación de datos para Chart.js
  const casesChartData = useMemo(() => ({
    labels: chartData.map(d => d.name),
    datasets: [{
      label: 'Casos',
      data: chartData.map(d => d.casos),
      borderColor: '#1A5FB4',
      backgroundColor: 'rgba(26, 95, 180, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 3,
      pointRadius: 4,
      pointBackgroundColor: '#1A5FB4',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
    }]
  }), [chartData]);

  const muniChartData = useMemo(() => ({
    labels: muniStats.map(d => d.name),
    datasets: [{
      label: 'Casos por Municipio',
      data: muniStats.map(d => d.count),
      backgroundColor: (context) => {
        const index = context.dataIndex;
        const colors = ['#0d3f75', '#1A5FB4', '#2069C4', '#2C7FE3', '#3E95FF'];
        return colors[index] || '#6B8E23';
      },
      borderRadius: 4,
      barThickness: 30,
    }]
  }), [muniStats]);

  const trendChartData = useMemo(() => {
    const currentMonthIndex = new Date().getMonth();
    const last6 = [];
    for (let i = 5; i >= 0; i--) {
      const idx = (currentMonthIndex - i + 12) % 12;
      last6.push(chartData[idx]);
    }
    return {
      labels: last6.map(d => d.name),
      datasets: [{
        data: last6.map(d => d.casos),
        borderColor: '#1A5FB4',
        backgroundColor: 'rgba(26, 95, 180, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
      }]
    };
  }, [chartData]);

  // Opciones base para los gráficos
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(13, 63, 117, 0.9)',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)', drawBorder: false },
        ticks: { font: { size: 11 }, stepSize: 5 }
      }
    }
  };

  const trendOptions = {
    ...baseOptions,
    plugins: { ...baseOptions.plugins, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  return (
    <div className="dashboard-container">
      <style>{`
        .pulse-dot {
          width: 8px;
          height: 8px;
          background-color: #66BB6A;
          border-radius: 50%;
          display: inline-block;
          position: relative;
        }
        .pulse-dot::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: #66BB6A;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          70% { transform: scale(3); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        .btn-primary:hover {
          background-color: #1a5fb4 !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(13, 63, 117, 0.3) !important;
        }
        .risk-card-mini {
          transition: transform 0.2s ease;
        }
        .risk-card-mini:hover {
          transform: translateX(5px);
        }
        .muni-rank-item {
          transition: transform 0.2s ease;
          padding: 4px 0;
        }
        .muni-rank-item:hover {
          transform: scale(1.02);
        }
      `}</style>
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-custom navbar-dark fixed-top">
        <div className="container-fluid ps-1">
          <Link className="navbar-brand d-flex flex-column align-items-center" to="/">
            <img src={LogoGeo} alt="GeoHCX" className="nav-logo-img mb-1" />
            <div className="d-flex flex-column justify-content-center align-items-center">
              <span className="fw-bold text-white fs-4 lh-sm">GEOHCX</span>
              <span className="text-white text-wrap mt-1" style={{ fontSize: "0.75rem", lineHeight: "1.1", maxWidth: "450px", textAlign: "center", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", letterSpacing: "0.2px" }}>
                Plataforma Digital para el Monitoreo, Registro y Alerta de Infecciones por Geohelmintos en Humanos en el departamento de Córdoba
              </span>
            </div>
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            onClick={handleNavCollapse}
            aria-controls="navbarNav"
            aria-expanded={!isNavCollapsed}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse justify-content-end`} id="navbarNav">
            <ul className="navbar-nav align-items-center">
              <li className="nav-item"><Link className="nav-link" to="/" onClick={() => setIsNavCollapsed(true)}>INICIO</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/quienes-somos" onClick={() => setIsNavCollapsed(true)}>QUIÉNES SOMOS</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/indicadores" onClick={() => setIsNavCollapsed(true)}>INDICADORES</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/mapa" onClick={() => setIsNavCollapsed(true)}>UBICACIÓN</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/consultar" onClick={() => setIsNavCollapsed(true)}>CONSULTAR</Link></li>
              <li className="nav-item"><Link className="nav-link" to="/ambiental" onClick={() => setIsNavCollapsed(true)}>DATOS AMBIENTALES</Link></li>
              <li className="nav-item ms-2">
                <Link to="/login" className="btn-login" onClick={() => setIsNavCollapsed(true)}>INICIAR SESIÓN</Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Slider */}
      <div style={{ marginTop: "0px" }}>
        <CustomSlider />
      </div>

      {loading ? (
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "80vh" }}>
          <div className="spinner-border text-success" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 fw-bold text-muted">Cargando datos del dashboard...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger mx-4 mt-4" role="alert">
          <h4 className="alert-heading">Error al cargar datos</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">Por favor, verifica la conexión con el servidor en <code>{Apiurl}</code> e intenta refrescar la página.</p>
          <button className="btn btn-outline-danger mt-3" onClick={() => window.location.reload()}>Reintentar</button>
        </div>
      ) : (
        <div className="container-fluid px-4">
          {/* Row 1: Top Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="stat-icon" viewBox="0 0 16 16">
                  <path d="M1.5 6a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z" />
                  <path d="M1.378 5.618a2.5 2.5 0 0 1 2.373-1.618h9a2.5 2.5 0 0 1 2.5 2.5v1a2.5 2.5 0 0 1-2.5 2.5h-9a2.5 2.5 0 0 1-2.373-1.618.5.5 0 0 0-.894.448 3.5 3.5 0 0 0 3.267 2.17h9a3.5 3.5 0 0 0 3.5-3.5v-1a3.5 3.5 0 0 0-3.5-3.5h-9a3.5 3.5 0 0 0-3.267 2.17.5.5 0 1 0 .894.448z" fillRule="evenodd" />
                </svg>
                <p className="stat-title">Casos Registrados</p>
              </div>
              <h2 className="stat-value">{stats.casos}</h2>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <i className="bi bi-geo-alt stat-icon"></i>
                <p className="stat-title">Municipios con Casos</p>
              </div>
              <h2 className="stat-value">{stats.municipios}</h2>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <i className="bi bi-exclamation-triangle stat-icon text-warning"></i>
                <p className="stat-title">Zonas de Alto Riesgo</p>
              </div>
              <h2 className="stat-value">{stats.zonasRiesgo}</h2>
            </div>
            <div className="stat-card" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.05)" }}>
              <div className="stat-header">
                <p className="stat-title" style={{ color: "#666" }}>Tendencia (6m)</p>
              </div>
              <div style={{ height: "40px", width: "100%" }}>
                <Line data={trendChartData} options={trendOptions} />
              </div>
            </div>
            {/* New Filter Card */}
            <div className="stat-card filters-card" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.05)", minWidth: '220px' }}>
              <div className="stat-header mb-2">
                <i className="bi bi-funnel stat-icon" style={{ fontSize: '1rem', color: '#6B8E23' }}></i>
                <p className="stat-title" style={{ color: "#444", fontSize: '0.9rem', fontWeight: '700' }}>Filtros de Análisis</p>
              </div>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center">
                  <span className="small text-muted me-2" style={{ fontSize: '0.7rem', minWidth: '60px' }}>Fecha:</span>
                  <select name="fecha" value={filters.fecha} onChange={handleFilterChange} className="form-select form-select-sm bg-light border-0" style={{ fontSize: '0.75rem', height: '28px', padding: '0 25px 0 10px' }}>
                    <option>Último Mes</option>
                    <option>Último Semestre</option>
                    <option>Año Actual</option>
                  </select>
                </div>
                <div className="d-flex align-items-center">
                  <span className="small text-muted me-2" style={{ fontSize: '0.7rem', minWidth: '60px' }}>Municipio:</span>
                  <select name="municipio" value={filters.municipio} onChange={handleFilterChange} className="form-select form-select-sm bg-light border-0" style={{ fontSize: '0.75rem', height: '28px', padding: '0 25px 0 10px' }}>
                    <option>Todos</option>
                    {municipiosCordoba.map(m => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="d-flex align-items-center">
                  <span className="small text-muted me-2" style={{ fontSize: '0.7rem', minWidth: '60px' }}>Riesgo:</span>
                  <select name="riesgo" value={filters.riesgo} onChange={handleFilterChange} className="form-select form-select-sm bg-light border-0" style={{ fontSize: '0.75rem', height: '28px', padding: '0 25px 0 10px' }}>
                    <option>Todos</option>
                    <option>Alto</option>
                    <option>Medio</option>
                    <option>Bajo</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Map and Side Panel */}
          <div className="dashboard-grid">
            <div className="main-column">
              <div className="map-container-new" style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="map-header">
                  <h4 className="section-title mb-0">Mapa de Laboratorios</h4>
                </div>
                <MapContainer
                  center={[8.3, -75.7]}
                  zoom={8.5}
                  style={{ flexGrow: 1, width: "100%", borderRadius: "8px", minHeight: "450px" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MarkerClusterGroup
                    chunkedLoading
                    showCoverageOnHover={false}
                    spiderfyOnMaxZoom={true}
                    iconCreateFunction={(cluster) => {
                      const count = cluster.getChildCount();
                      return L.divIcon({
                        html: `<div style="position: relative; background-color: #0d6efd; width: 45px; height: 45px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.4);">
                                 <img src="${TestResultIcon}" style="width: 26px; height: 26px; object-fit: contain;" alt="Lab" />
                                 <div style="position: absolute; top: -10px; right: -10px; background-color: #f44336; color: white; border-radius: 50%; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">
                                   ${count}
                                 </div>
                               </div>`,
                        className: 'custom-cluster-icon',
                        iconSize: [45, 45],
                        iconAnchor: [22, 22]
                      });
                    }}
                  >
                    {laboratorios.map((lab, idx) => {
                      const labIcon = L.divIcon({
                        html: `<div style="background-color: #0d6efd; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2.5px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.35);">
                                 <img src="${TestResultIcon}" style="width: 20px; height: 20px; object-fit: contain;" alt="Lab" />
                               </div>`,
                        className: 'custom-lab-icon',
                        iconSize: [34, 34],
                        iconAnchor: [17, 17]
                      });

                      return (
                        <Marker
                          key={idx}
                          position={[lab.latitud || 8.75, lab.longitud || -75.88]}
                          icon={labIcon}
                        >
                          <Popup>
                            <div className="p-1">
                              <strong className="text-primary d-block mb-1">{lab.nombre_laboratorio}</strong>
                              <div className="small mb-1"><i className="bi bi-geo-alt-fill me-1"></i> {lab.municipio}, {lab.departamento}</div>
                              {lab.direccion && <div className="small mb-1"><i className="bi bi-map-fill me-1"></i> {lab.direccion}</div>}
                              {lab.telefonos && <div className="small"><i className="bi bi-telephone-fill me-1"></i> {lab.telefonos}</div>}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                </MapContainer>
                <div className="map-legend mt-3">
                  <div className="legend-item">
                    <div style={{ backgroundColor: "#0d6efd", width: "24px", height: "24px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", marginRight: "8px" }}>
                      <img src={TestResultIcon} style={{ width: "14px", height: "14px", objectFit: "contain" }} alt="Lab Icon" />
                    </div>
                    Laboratorios Registrados
                  </div>
                </div>
              </div>
            </div>

            <div className="side-column">
              <div className="alerts-panel p-3 d-flex flex-column" style={{ background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)", borderRadius: "15px", border: "1px solid rgba(0,0,0,0.05)" }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="m-0 fs-6 fw-bold color-primary" style={{ color: "#0d3f75" }}>Monitor de Riesgos</h5>
                  <div className="pulse-container">
                    <span className="pulse-dot"></span>
                    <i className="bi bi-activity text-muted ms-2"></i>
                  </div>
                </div>

                <div className="risk-cards-grid d-flex flex-column gap-2 mb-4">
                  <div className="risk-card-mini d-flex align-items-center p-2 rounded-3" style={{ background: "#fff", borderLeft: "4px solid #EF5350", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                    <div className="risk-icon-circle me-3" style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#EF5350', boxShadow: '0 0 8px rgba(239, 83, 80, 0.4)' }}></div>
                    <div className="flex-grow-1">
                      <span className="small text-muted d-block">Alto Riesgo</span>
                      <strong className="text-danger">{stats.zonasRiesgo} Municipios</strong>
                    </div>
                  </div>

                  <div className="risk-card-mini d-flex align-items-center p-2 rounded-3" style={{ background: "#fff", borderLeft: "4px solid #FFA726", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                    <div className="risk-icon-circle me-3" style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#FFA726' }}></div>
                    <div className="flex-grow-1">
                      <span className="small text-muted d-block">Riesgo Medio</span>
                      <strong className="text-warning">{stats.mediumRisk} Municipios</strong>
                    </div>
                  </div>

                  <div className="risk-card-mini d-flex align-items-center p-2 rounded-3" style={{ background: "#fff", borderLeft: "4px solid #66BB6A", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                    <div className="risk-icon-circle me-3" style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#66BB6A' }}></div>
                    <div className="flex-grow-1">
                      <span className="small text-muted d-block">Bajo Riesgo</span>
                      <strong className="text-success">{stats.lowRisk} Municipios</strong>
                    </div>
                  </div>
                </div>

                <div className="mt-auto">
                  <Link to="/mapa" className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2 rounded-pill fw-bold shadow-sm" style={{ backgroundColor: "#0d3f75", border: "none", transition: "all 0.3s ease" }}>
                    <i className="bi bi-map-fill"></i>
                    EXPLORAR MAPA COMPLETO
                  </Link>
                </div>
              </div>

              <div className="table-card" style={{ marginTop: "20px", border: "1px solid rgba(0,0,0,0.05)" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-bar-chart-fill text-primary" style={{ fontSize: '1.2rem' }}></i>
                  <h5 className="section-title m-0" style={{ fontSize: '0.95rem', fontWeight: '800', color: '#0d3f75', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Análisis por Municipio
                  </h5>
                </div>
                {muniStats.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {muniStats.map((ms, i) => (
                      <div key={i} className="muni-rank-item">
                        <div className="d-flex justify-content-between align-items-end mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge rounded-circle bg-light text-primary border" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>
                              {i + 1}
                            </span>
                            <span className="fw-bold" style={{ fontSize: '0.85rem', color: '#444', fontFamily: "'Inter', sans-serif" }}>{ms.name}</span>
                          </div>
                          <span className="fw-bolder text-primary" style={{ fontSize: '0.9rem' }}>{ms.count} <small className="text-muted fw-normal" style={{ fontSize: '0.65rem' }}>casos</small></span>
                        </div>
                        <div className="progress" style={{ height: '6px', backgroundColor: '#f0f0f0', borderRadius: '10px' }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${Math.min(ms.percentage * 2.5, 100)}%`,
                              backgroundColor: i === 0 ? '#0d3f75' : i < 3 ? '#1A5FB4' : '#6B8E23',
                              borderRadius: '10px',
                              transition: 'width 1s ease-in-out'
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bi bi-clipboard-x text-muted opacity-25" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted small mt-2">No hay datos de distribución geográfica</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bottom-grid mt-4">
            <div className="chart-card">
              <h5 className="section-title">Reporte de Casos Recientes</h5>
              <div style={{ height: "180px" }}>
                <Line data={casesChartData} options={baseOptions} />
              </div>
            </div>

            <div className="table-card border-0" style={{ boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-bar-chart-line-fill text-primary"></i>
                <h5 className="section-title m-0" style={{ fontSize: '0.9rem', fontWeight: '700', color: '#444' }}>Top 5 Municipios con Más Casos</h5>
              </div>
              <div style={{ height: "180px", width: "100%" }}>
                <Bar data={muniChartData} options={baseOptions} />
              </div>
            </div>

            <div className="news-card p-0 overflow-hidden position-relative" style={{ height: "240px" }}>
              <div
                className="position-absolute w-100 h-100"
                style={{
                  backgroundImage: `url(${SliderMonitoring})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.4)'
                }}
              ></div>

              <div className="position-relative z-1 h-100 d-flex flex-column justify-content-center p-4 text-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="badge bg-warning text-dark fw-bold">Noticias de Interés</span>
                  <div className="d-flex gap-1">
                    {newsSliderData.map((_, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: idx === currentNews ? '20px' : '6px',
                          height: '6px',
                          backgroundColor: idx === currentNews ? 'var(--action-gold)' : 'rgba(255,255,255,0.3)',
                          borderRadius: '3px',
                          transition: 'all 0.3s'
                        }}
                      ></div>
                    ))}
                  </div>
                </div>

                <div className="mt-2" key={currentNews} style={{ animation: 'fadeIn 0.5s ease-in-out' }}>
                  <h5 className="fw-bold mb-2 text-truncate" style={{ fontSize: '1.1rem' }}>{newsSliderData[currentNews].title}</h5>
                  <p className="small mb-3 text-white-50" style={{ lineHeight: '1.4', fontSize: '0.85rem', minHeight: '3em' }}>
                    {newsSliderData[currentNews].desc}
                  </p>
                  <a
                    href={newsSliderData[currentNews].link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-light rounded-pill px-3 fw-bold"
                  >
                    Leer Más <i className="bi bi-arrow-right-short"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '2rem' }}>
            <LogosFooter />
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboardmain;
